<template>
  <div>
    <div v-if="isMine(tournament)">
      <md-list>
        <!-- General competitors actions -->
        <md-toolbar>
          <md-button @click="showAddCompetitorsDialog = true" class="md-icon-button">
            <md-icon>group_add</md-icon>
            <md-tooltip>Add competitors</md-tooltip>
          </md-button>
        </md-toolbar>
        <AddCompetitorsDialog
          :tournament="tournament"
          :show="showAddCompetitorsDialog"
          @onClose="showAddCompetitorsDialog = false"
        />

        <!-- Rankings updater -->
        <md-list-item md-expand class="rankings">
          <md-icon>sort</md-icon>
          <span class="md-list-item-text">Adjust Rankings</span>
          <md-list slot="md-expand" class="rankings">
            <md-list-item>
              <md-toolbar>
                <md-button @click="resetRankings" class="md-icon-button">
                  <md-icon>cancel</md-icon>
                  <md-tooltip>Reset rankings</md-tooltip>
                </md-button>
                <md-button
                  @click="commitRankings"
                  :disabled="!rankingsChanged"
                  class="md-icon-button"
                >
                  <md-icon>save</md-icon>
                  <md-tooltip>Commit new rankings</md-tooltip>
                </md-button>
              </md-toolbar>
            </md-list-item>
            <md-list-item>
              <div>
                <p
                  class="ranking-instructions"
                >Drag and drop the competitors in the desired ranking, stronger competitors at the top. Be sure to commit the rankings before generating the draw.</p>
              </div>
            </md-list-item>
            <md-list-item>
              <draggable
                class="list-group"
                element="ul"
                v-model="rankedCompetitors"
                :options="dragOptions"
                @start="drag = true"
                @end="drag = false"
              >
                <transition-group type="transition" :name="!drag ? 'flip-list' : null">
                  <li
                    class="list-group-item"
                    v-for="element in rankedCompetitors"
                    :key="element.id"
                  >
                    <md-icon>person</md-icon>
                    <span>{{ element.name }}</span>
                    <span class="rank">{{element.rank}}</span>
                  </li>
                </transition-group>
              </draggable>
            </md-list-item>
          </md-list>
        </md-list-item>
      </md-list>
    </div>

    <!-- Competitor lists -->
    <div v-if="tournament && tournament.competitors.length>0">
      <div class="flex-container">
        <CompetitorList
          title="Not ready"
          :competitors="pendingCompetitors"
          :tournament="tournament"
          class="flex-item"
        />
        <CompetitorList
          title="Ready"
          :competitors="readyCompetitors"
          :tournament="tournament"
          class="flex-item"
        />
        <CompetitorList
          title="Eliminated"
          :competitors="eliminatedCompetitors"
          :tournament="tournament"
          class="flex-item"
        />
        <CompetitorList
          title="Inactive"
          :competitors="inactiveCompetitors"
          :tournament="tournament"
          class="flex-item"
        />
      </div>
    </div>
    <div v-else>
      <md-empty-state md-icon="people_outline" md-label="No Competitors yet"></md-empty-state>
    </div>
  </div>
</template>

<script>
import gql from "graphql-tag";
import { tournamentCompetitorFragment } from "@/graphql/tournament";
import AddCompetitorsDialog from "@/components/dialogs/addCompetitorsDialog.vue";
import CompetitorList from "./competitorList.vue";
import { TournamentTools } from "@/js/helper";
import draggable from "vuedraggable";

function nameComparer(a, b) {
  return a.name < b.name ? -1 : b.name < a.name ? 1 : rankComparer(a, b);
}
function rankComparer(a, b) {
  return a.rank < b.rank ? -1 : b.rank < a.rank ? 1 : 0;
}

export default {
  name: "TournamentCompetitors",
  components: { CompetitorList, AddCompetitorsDialog, draggable },
  props: { tournament: Object },
  data: function() {
    return {
      showAddCompetitorsDialog: false,
      rankings: [],
      drag: false,
      rankingsChanged: false
    };
  },
  computed: {
    rankedCompetitors: {
      get() {
        if (!this.rankings || this.rankings.length === 0)
          this.rankings = this.tournament.competitors
            .filter(x => !x.isBye)
            .sort(rankComparer)
            .map((x, i) => {
              const y = {};
              Object.assign(y, x);
              return y;
            });

        return this.rankings;
      },
      set(value) {
        value.forEach((x, i) => {
          x.rank = (i + 1) * 10;
        });
        this.rankings = value;
        this.rankingsChanged = true;
      }
    },
    dragOptions() {
      return {
        animation: 200,
        group: "description",
        disabled: false,
        ghostClass: "ghost"
      };
    },
    inactiveCompetitors: {
      get() {
        return this.tournament.competitors
          .filter(x => !x.isActive && !x.isBye)
          .sort(nameComparer);
      }
    },
    readyCompetitors: {
      get() {
        return this.tournament.competitors
          .filter(
            x => x.isActive && !x.isBye && x.isReadyToCompete && !x.isEliminated
          )
          .sort(nameComparer);
      }
    },
    eliminatedCompetitors: {
      get() {
        return this.tournament.competitors
          .filter(x => x.isActive && !x.isBye && x.isEliminated)
          .sort(nameComparer);
      }
    },
    pendingCompetitors: {
      get() {
        return this.tournament.competitors
          .filter(
            x =>
              x.isActive && !x.isBye && !x.isReadyToCompete && !x.isEliminated
          )
          .sort(nameComparer);
      }
    }
  },
  methods: {
    isMine: TournamentTools.isMine,
    resetRankings() {
      this.rankings = [];
      this.rankingsChanged = false;
    },
    commitRankings() {
      return this.$apollo
        .mutate({
          mutation: gql`
            mutation($tournamentId: ID!, $orderedCompetitorIds: [ID!]!) {
              updateRankings(
                tournamentId: $tournamentId
                orderedCompetitorIds: $orderedCompetitorIds
              ) {
                ...TournamentCompetitor
              }
            }
            ${tournamentCompetitorFragment}
          `,
          variables: {
            tournamentId: this.tournament.id,
            orderedCompetitorIds: this.rankings.map(x => x.id)
          }
        })
        .then(result => {
          //this.$root.$emit("updateTournament", this.tournament.id);
          this.$store.commit("setNotification", `Rankings have been committed`);
          this.rankingsChanged = false;
        })
        .catch(err => {
          console.log(err);
          window.alert("Failed to commit rankings");
        });
    }
  },
  watch: {
    tournament(t) {
      this.resetRankings();
    }
  }
};
</script>

<style lang="scss" scoped>
.flex-container {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
}
.flex-item {
  display: flex;
  flex-direction: column;
  flex-basis: 100%;
  flex: 1;
}

.rankings {
  background-color: rgb(131, 131, 131) !important;
}
.rank {
  float: right;
  text-align: right;
}
.ranking-instructions {
  white-space: normal;
  overflow-wrap: break-word;
  word-wrap: break-word;
}

.flip-list-move {
  transition: transform 0.5s;
}
.no-move {
  transition: transform 0s;
}

.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}
.list-group {
  min-height: 20px;
  list-style: none;
  list-style-position: inside;
  padding-left: 0;
}
.list-group-item {
  cursor: move;
  margin: 0.25em;
  padding-left: 1em;
  padding-right: 1em;
  padding-top: 0.5em;
  padding-bottom: 0.5em;
  width: 300px;
  background-color: rgb(82, 85, 252);
  border: solid;
  border-radius: 1em;
}
.list-group-item i {
  cursor: pointer;
}
</style>


// WEBPACK FOOTER //
// tournamentCompetitors.vue?3e0e3e1a